import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Info, Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'text' | 'danger' | 'danger-solid' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
}

export function buttonClass({ variant = 'secondary', size = 'md', block }: CommonProps, extra?: string): string {
  return ['btn', `btn--${variant}`, size !== 'md' && `btn--${size}`, block && 'btn--block', extra].filter(Boolean).join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, CommonProps {
  loading?: boolean;
}

/** Loading keeps the button width and swaps the label for a spinner (§8.1). */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, block, icon, iconRight, loading, className, children, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={buttonClass({ variant, size, block }, className)}
      disabled={disabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
      {loading && (
        <span className="btn__spinner" aria-hidden="true">
          <Loader2 size={18} className="spin" />
        </span>
      )}
    </button>
  );
});

export function ButtonLink({ variant, size, block, icon, iconRight, className, children, ...rest }: LinkProps & CommonProps) {
  return (
    <Link className={buttonClass({ variant, size, block }, className)} {...rest}>
      {icon}
      {children}
      {iconRight}
    </Link>
  );
}

/**
 * A disabled action never stands alone: the reason sits right next to it and is linked via aria-describedby
 * (UI spec principle 4, §8.1).
 */
export function ActionWithReason({ reason, id, children }: { reason: ReactNode | null | false; id: string; children: ReactNode }) {
  return (
    <div className="action-with-reason">
      {children}
      {reason && (
        <span className="disabled-reason" id={id}>
          <Info size={16} aria-hidden="true" />
          {reason}
        </span>
      )}
    </div>
  );
}
